import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import continuousServiceService from '../../services/continuousServiceService';
import CloseServiceModal from './CloseServiceModal';
import Toast from '../../components/Common/Toast';
import TableWithMonthGroups from '../../components/Common/TableWithMonthGroups';
import { formatDateLocaleLima } from '../../utils/dateUtils';
import BuyerUserCell from '../../components/Common/BuyerUserCell';
import ComprasSiteFilterTabs from '../../components/Common/ComprasSiteFilterTabs';
import { useComprasSiteListParams, useComprasSiteViewState } from '../../utils/comprasPurchaseSite';

const ContinuousServices = () => {
  const { siteView, onSiteViewChange } = useComprasSiteViewState();
  const siteListParams = useComprasSiteListParams(siteView);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingServices, setLoadingServices] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [supplierSearch, setSupplierSearch] = useState('');
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [serviceToClose, setServiceToClose] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const hasLoadedInitialData = useRef(false);
  const previousFilters = useRef({
    statusFilter: 'all',
    supplierSearch: '',
    searchTerm: '',
    dateFrom: '',
    dateTo: ''
  });
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!hasLoadedInitialData.current) {
      hasLoadedInitialData.current = true;
      previousFilters.current = { statusFilter, supplierSearch, searchTerm, dateFrom, dateTo };
      loadContinuousServices();
    }
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      previousFilters.current = { statusFilter, supplierSearch, searchTerm, dateFrom, dateTo };
      return;
    }

    if (hasLoadedInitialData.current) {
      const filtersChanged =
        previousFilters.current.statusFilter !== statusFilter ||
        previousFilters.current.supplierSearch !== supplierSearch ||
        previousFilters.current.searchTerm !== searchTerm ||
        previousFilters.current.dateFrom !== dateFrom ||
        previousFilters.current.dateTo !== dateTo;

      if (filtersChanged) {
        previousFilters.current = { statusFilter, supplierSearch, searchTerm, dateFrom, dateTo };
        loadContinuousServices();
      }
    }
  }, [statusFilter, supplierSearch, searchTerm, dateFrom, dateTo]);

  useEffect(() => {
    if (hasLoadedInitialData.current) {
      loadContinuousServices();
    }
  }, [siteView]);

  const loadContinuousServices = async () => {
    setLoadingServices(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const searchParts = [supplierSearch.trim(), searchTerm.trim()].filter(Boolean);
      if (searchParts.length) params.search = searchParts.join(' ');
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      Object.assign(params, siteListParams);

      const response = await continuousServiceService.getContinuousServices(params);
      
      if (response.success && response.data) {
        setServices(response.data);
      }
    } catch (error) {
      console.error('Error al cargar servicios continuos:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar los servicios continuos',
        type: 'error'
      });
    } finally {
      setLoadingServices(false);
    }
  };

  const handleCloseClick = (service) => {
    setServiceToClose(service);
    setIsCloseModalOpen(true);
  };

  const handleCloseConfirm = async (endDate) => {
    if (!serviceToClose || !endDate) return;

    setLoading(true);
    try {
      const response = await continuousServiceService.closeContinuousService(serviceToClose.id, { end_date: endDate });
      
      if (response.success) {
        setToast({
          isOpen: true,
          message: 'Servicio continuo cerrado exitosamente',
          type: 'success'
        });
        setIsCloseModalOpen(false);
        setServiceToClose(null);
        loadContinuousServices();
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al cerrar el servicio continuo',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al cerrar servicio continuo:', error);
      setToast({
        isOpen: true,
        message: 'Error al cerrar el servicio continuo',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { label: 'Abierto', className: 'bg-green-100 text-green-800' },
      closed: { label: 'Cerrado', className: 'bg-gray-100 text-gray-800' }
    };

    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const creatorOptions = useMemo(() => {
    const map = new Map();
    services.forEach((row) => {
      const id = row.creator?.id ?? row.created_by;
      const name = row.creator?.name;
      if (id && name && !map.has(String(id))) map.set(String(id), name);
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'es'));
  }, [services]);

  const filteredServices = useMemo(() => {
    if (userFilter === 'all') return services;
    return services.filter((row) => String(row.creator?.id ?? row.created_by ?? '') === String(userFilter));
  }, [services, userFilter]);

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Servicios Continuos</h1>
          <p className="text-sm text-gray-600 mt-1">Gestiona servicios que se desarrollan a lo largo del tiempo</p>
        </div>
        <Link
          to="/continuous-services/create"
          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium flex items-center gap-2"
        >
          <span>+</span>
          Nuevo Servicio Continuo
        </Link>
      </div>

      <ComprasSiteFilterTabs siteView={siteView} onSiteViewChange={onSiteViewChange} />

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Código..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Proveedor</label>
            <input
              type="text"
              value={supplierSearch}
              onChange={(e) => setSupplierSearch(e.target.value)}
              placeholder="Buscar por nombre de proveedor..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">Todos</option>
              <option value="open">Abierto</option>
              <option value="closed">Cerrado</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Usuario</label>
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">Todos</option>
              {creatorOptions.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loadingServices ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : filteredServices.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>No se encontraron servicios continuos</p>
            <Link
              to="/continuous-services/create"
              className="mt-4 inline-block text-teal-600 hover:text-teal-700 font-medium"
            >
              Crear el primero
            </Link>
          </div>
        ) : (
          <TableWithMonthGroups
            data={filteredServices}
            getDateForGrouping={(s) => s.start_date}
            defaultCollapsed
            columnCount={7}
            emptyMessage="No se encontraron servicios continuos"
            headerRow={
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Proveedor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Inicio</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Fin</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto Acumulado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            }
            renderRow={(service) => (
              <tr key={service.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <Link to={`/continuous-services/${service.id}`} className="text-teal-600 hover:text-teal-800 font-medium">
                    {service.code || `SC-${service.id}`}
                  </Link>
                  <BuyerUserCell creator={service.creator} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{service.supplier?.name || 'N/A'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {service.start_date ? formatDateLocaleLima(service.start_date) : 'N/A'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {service.end_date ? formatDateLocaleLima(service.end_date) : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {formatCurrency(service.total_amount)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(service.status)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end gap-2">
                    <Link to={`/continuous-services/${service.id}`} className="text-teal-600 hover:text-teal-900" title="Ver detalle">Ver</Link>
                    {service.status === 'open' && (
                      <button onClick={() => handleCloseClick(service)} className="text-orange-600 hover:text-orange-900" title="Cerrar servicio">Cerrar</button>
                    )}
                  </div>
                </td>
              </tr>
            )}
          />
        )}
      </div>

      {/* Modal para cerrar: pide fecha de cierre */}
      <CloseServiceModal
        isOpen={isCloseModalOpen}
        onClose={() => {
          setIsCloseModalOpen(false);
          setServiceToClose(null);
        }}
        onConfirm={handleCloseConfirm}
        serviceName={serviceToClose?.code || (serviceToClose ? `SC-${serviceToClose.id}` : '')}
        loading={loading}
      />

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default ContinuousServices;
